import { FC, useContext } from 'react';
import { ProposalFlight } from 'ts/shared/interfaces/ProposalData';
import { checkAHAVI, formatPriceCurrency, getCurrency, getLogoUrl } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import ProposalEditHead from './ProposalEditHead';
import useRemovedItem from './useRemovedItem';
import { dateFormat } from 'utils/shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCartFlatbedSuitcase,
  faPersonWalkingLuggage,
  faSuitcaseRolling,
} from '@fortawesome/free-solid-svg-icons';
import { formatFltPersons } from 'utils/shared/proposal';
import { MainContext } from 'contexts/MainContext';

interface ProposalFltProps {
  flight: ProposalFlight;
  hideWrapper?: boolean;
  displayPrice: boolean;
  proposalId: string;
  isPackage?: boolean;
  currency?: string;
  renderBaggage?: () => React.ReactNode;
}

const getBaggageInfo = (flight: ProposalFlight, currency: string, t: any, isPackage?: boolean) => {
  const baggageInfo: { text: string; icon: any; color: string; type: number }[][] = [];
  const green = '#366e34';
  const red = '#ef4444';
  let addedHandbagInfo: boolean[] = [];

  let paxCodes = Array.from(
    new Set(
      flight.listOfCatalogPrice
        ?.filter((item: any) => item.paxCode > 0)
        .map((item: any) => item.paxCode)
    )
  );

  if (!paxCodes.length && flight.listOfCatalogPrice?.length > 0) {
    paxCodes = [0];
  }

  paxCodes.forEach((paxCode, paxCodeIndex) => {
    baggageInfo.push([]);
    addedHandbagInfo.push(false);
    const list = flight.listOfCatalogPrice?.filter(
      (item: any) => item.paxCode === paxCode || item.paxCode === 0
    );
    if (list?.length > 0) {
      for (const flyBaggagePrice of list) {
        const isAnyHandbag = list.some(
          (item: any) =>
            item.maxWeightHandbag > 0 && (item.paxCode === 0 || item.paxCode === paxCode)
        );

        if (flyBaggagePrice.displayPrice && flyBaggagePrice.displayPrice.value > 0) {
          const symbol = getCurrency(currency || 'usd');
          const price = flyBaggagePrice.displayPrice[currency?.toLowerCase() || 'usd'];

          baggageInfo[paxCodeIndex].push({
            text: t('flight.incl_baggage', { price: formatPriceCurrency(price, symbol) }),
            icon: faCartFlatbedSuitcase,
            color: green,
            type: 3,
          });
        } else if (
          !addedHandbagInfo[paxCodeIndex] &&
          (!isAnyHandbag || flyBaggagePrice.maxWeightHandbag > 0)
        ) {
          addedHandbagInfo[paxCodeIndex] = true;
          if (flyBaggagePrice.maxWeightHandbag > 0) {
            baggageInfo[paxCodeIndex].push({
              text: t('flight.incl_handbag_kg', { kg: flyBaggagePrice.maxWeightHandbag }),
              icon: faSuitcaseRolling,
              color: green,
              type: 1,
            });
          } else if (flyBaggagePrice.maxWeightHandbag !== -1) {
            baggageInfo[paxCodeIndex].push({
              text: t('flight.without_handbag'),
              icon: faSuitcaseRolling,
              color: red,
              type: 1,
            });
          }

          if (flyBaggagePrice.maxTotalWeight > 0) {
            baggageInfo[paxCodeIndex].push({
              text: t('flight.incl_suitcase_kg', { kg: flyBaggagePrice.maxTotalWeight }),
              icon: faCartFlatbedSuitcase,
              color: green,
              type: 3,
            });
          } else if (flyBaggagePrice.pieces > 0) {
            baggageInfo[paxCodeIndex].push({
              text: t('flight.incl_suitcase'),
              icon: faCartFlatbedSuitcase,
              color: green,
              type: 3,
            });
          }
        } else if (
          flyBaggagePrice.maxTotalWeightUnit === '1' &&
          flyBaggagePrice.type === 'fromMaxWeight'
        ) {
          baggageInfo[paxCodeIndex].push({
            text: t('flight.incl_baggage', { price: '' }).slice(0, -1),
            icon: faCartFlatbedSuitcase,
            color: green,
            type: 3,
          });
        } else if (
          flyBaggagePrice.maxTotalWeightUnit === '1' &&
          flyBaggagePrice.type === 'fromMaxWeightTrolley'
        ) {
          baggageInfo[paxCodeIndex].push({
            text: t('flight.trolley_included'),
            icon: faPersonWalkingLuggage,
            color: green,
            type: 2,
          });
        }
      }
    } else {
      baggageInfo[paxCodeIndex].push({
        text: t('flight.no_baggage'),
        icon: faCartFlatbedSuitcase,
        color: red,
        type: 0,
      });
    }
  });

  return baggageInfo.map((bag, index) => (
    <div key={index}>
      {!isPackage && flight.listOfCatalogPrice?.some((item: any) => item.paxCode !== 0) && (
        <span>{t('passenger.passenger') + ' ' + (index + 1) + ': '}</span>
      )}
      {bag
        .sort((a, b) => a.type - b.type)
        .map((item, index) => (
          <div
            key={index}
            style={{
              color: item.color,
              display: 'inline-flex',
              alignItems: 'center',
              marginInlineEnd: 5,
            }}
          >
            <FontAwesomeIcon
              icon={item.icon}
              style={{ width: 14, height: 14, marginInlineEnd: 3 }}
            />
            {item.text}
            {index < bag.length - 1 && ', '}
          </div>
        ))}
    </div>
  ));
};

const ProposalFlt: FC<ProposalFltProps> = ({ flight, hideWrapper, ...props }) => {
  const { t } = useTranslation();
  const gds = flight.gdsFlyAirs;
  const removedItem = useRemovedItem(props.proposalId);
  const { cdnUrl } = useContext(MainContext);

  return (
    <div className="pr-hotel-card-wr">
      <div
        className={(hideWrapper ? '' : 'pr-hotel-card') + (removedItem ? ' pr-hotel-removed' : '')}
      >
        <div className="pr-break-avoid">
          {!hideWrapper && (
            <div className="pr-hotel-card-title pr-flex-between">
              <div className={'pr-flex-row' + (flight.gdsFlyAirs.length > 2 ? ' pr-text-sm' : '')}>
                <span className="pr-mr-5">{t('flight.flight')}:</span>

                <div className="pr-flex-row">
                  <span className="pr-mr-5">{flight.gdsFlyAirs[0].departureAirport.code}</span>
                  <span>{dateFormat(flight.gdsFlyAirs[0].departureDateTime, 'dd/MM', 'en')}</span>,
                  <span className="pr-ph-3">{formatFltPersons(flight, t)}</span>
                </div>
              </div>
              <ProposalEditHead proposalId={props.proposalId} displayPrice={props.displayPrice} />
            </div>
          )}
          <div className="pr-lh">
            <div className="pr-mb-5">
              <span className="pr-status">
                {flight.flightType === 'OneWay'
                  ? t('flight.one_way')
                  : flight.flightType === 'RoundTrip'
                    ? t('flight.round_trip')
                    : t('flight.multiple_destinations')}
              </span>

              <span className="pr-ph-10 pr-bold">
                {flight.remarks?.[0]?.name?.toUpperCase().includes('ALP') &&
                  flight.remarks?.[1]?.name
                  ? flight.remarks?.[1]?.name
                  : ''}
              </span>
            </div>
            <div>
              {dateFormat(gds[0].departureDateTime, 'EEE, dd/MM HH:mm', 'en')} -{' '}
              {dateFormat(gds[gds.length - 1].arrivalDateTime, 'EEE, dd/MM HH:mm', 'en')}
            </div>
            <div>
              {t('search.adults')}: {flight.pax.adults}{' '}
              {!!flight.pax.children && (
                <>
                  , {t('search.children')}: {flight.pax.children}
                </>
              )}
              {!!flight.pax.infant && (
                <>
                  , {t('search.infants')}: {flight.pax.infant}
                </>
              )}
              {!!flight.pax.senior && (
                <>
                  , {t('search.senior')}: {flight.pax.senior}
                </>
              )}
            </div>

            <div className="pr-bold">
              {getBaggageInfo(flight, props.currency || 'usd', t, props.isPackage)}
            </div>
            {props?.renderBaggage?.()}
            {props.isPackage && <div className="pr-bold"></div>}
          </div>
        </div>

        <div className="pr-table-wr">
          <table className="pr-table">
            <thead>
              <tr>
                <th>{t('filter.airline')}</th>
                <th>{t('flight.flight')}</th>
                <th>{t('flight.dep_time')}</th>
                <th>{t('flight.dep_airport')}</th>
                <th>{t('flight.arr_time')}</th>
                <th>{t('flight.arr_airport')}</th>
                <th>{t('flight.aircraft')}</th>
                <th>{t('filter.duration')}</th>
              </tr>
            </thead>
            <tbody>
              {flight.gdsFlyAirs.map((gds, subindex) => (
                <tr key={subindex}>
                  <td className="position-relative" width={100}>
                    <img
                      className="pr-flt-logo"
                      src={getLogoUrl(
                        cdnUrl + '/backend/s3/prod.beprotravel/cdn/',
                        gds.airLine.code
                      )}
                      alt={gds.airLine.code}
                    />
                  </td>
                  <td>
                    {gds.airLine.code} {gds.flightNumber}
                  </td>
                  <td>{dateFormat(gds.departureDateTime, 'dd/MM HH:mm')}</td>
                  <td>
                    <span className="text-bold">{gds.departureAirport.code} </span>
                    {gds.departureAirport.name}
                  </td>
                  <td>{dateFormat(gds.arrivalDateTime, 'dd/MM HH:mm')}</td>
                  <td>
                    <span className="text-bold">{gds.arrivalAirport.code} </span>
                    {gds.arrivalAirport.name}
                  </td>
                  <td>{gds.aircraftType?.code}</td>
                  <td>{gds.durationTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {flight.displayNote?.code && !props.isPackage && (
          <div
            className={'pr-notes' + (checkAHAVI(flight.displayNote?.name) ? ' pr-rtl' : ' pr-ltr')}
          >
            <div>{t('notes')}: </div>
            <div
              className="pr-bold"
              dangerouslySetInnerHTML={{
                __html: flight.displayNote?.code.replaceAll(/\n/g, '<br />'),
              }}
            ></div>
          </div>
        )}
      </div>
      {!hideWrapper && <div className="pr-divider"></div>}
    </div>
  );
};

export default ProposalFlt;
